import React, { useEffect, useRef, useState } from 'react';
import { Search, MapPin, Loader2 } from 'lucide-react';

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Lazy-load the Google Maps Places library once per page lifetime.
let mapsScriptPromise = null;
function loadMapsLib() {
  if (typeof window === 'undefined') return Promise.reject('no window');
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (mapsScriptPromise) return mapsScriptPromise;
  mapsScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${KEY}&libraries=places`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return mapsScriptPromise;
}

/**
 * Places Autocomplete input.
 * Calls onPlacePicked({ name, formatted_address, latitude, longitude }) when
 * the user selects a suggestion.
 *
 * Props:
 *   value    - controlled current text
 *   onChange - (text) => void
 *   placeholder
 *   onPlacePicked
 *   testId
 */
export default function PlacesAutocomplete({ value, onChange, onPlacePicked, placeholder, testId }) {
  const inputRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    loadMapsLib()
      .then(() => alive && setReady(true))
      .catch(() => alive && setError('Could not load Google Places. Check API key & billing.'));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!ready || !inputRef.current) return;
    const auto = new window.google.maps.places.Autocomplete(inputRef.current, {
      fields: ['name', 'formatted_address', 'geometry']
    });
    const listener = auto.addListener('place_changed', () => {
      const p = auto.getPlace();
      if (!p?.geometry?.location) return;
      onPlacePicked?.({
        name: p.name || '',
        formatted_address: p.formatted_address || '',
        latitude: p.geometry.location.lat(),
        longitude: p.geometry.location.lng()
      });
    });
    return () => {
      window.google?.maps?.event?.removeListener?.(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  return (
    <div className="relative" data-testid={testId}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
      <input
        ref={inputRef}
        type="text"
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder || 'Search places…'}
        className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-blue-400"
        data-testid={testId ? `${testId}-input` : undefined}
        autoComplete="off"
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2">
        {!ready && !error && <Loader2 size={14} className="text-gray-400 animate-spin" />}
        {ready && <MapPin size={14} className="text-blue-500" />}
      </span>
      {error && <p className="text-[10px] text-red-600 mt-1">{error}</p>}
    </div>
  );
}
